<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { cloudApi } from '@/api/index.js'

const loading = ref(false)
const saving = ref(false)
const savingId = ref('')
const editingId = ref('')
const errorMessage = ref('')
const successMessage = ref('')
const items = ref([])
const testResults = reactive({})
const candidateTested = ref(false)
const candidateTestMessage = ref('')

const defaults = {
  provider: 'deepseek',
  label: 'AI 营销项目经理（DeepSeek）',
  base_url: 'https://api.deepseek.com',
  auth_type: 'bearer',
  api_key: '',
  status: 'disabled',
  chat: { enabled: true, request_mode: 'openai_chat_completions', endpoint: '/chat/completions', model: '', timeout_seconds: 180 },
  image: { enabled: false, request_mode: 'openai_images_generations', endpoint: '/images/generations', model: '', timeout_seconds: 420 },
  responses: { enabled: false, request_mode: 'openai_responses', endpoint: '/responses', model: '', timeout_seconds: 180 },
}
const form = reactive(structuredClone(defaults))
const formTitle = computed(() => editingId.value ? `编辑 ${form.provider}` : '新增系统供应商')

onMounted(load)

async function load() {
  loading.value = true
  errorMessage.value = ''
  try {
    const payload = await cloudApi.get('/api/admin/system-model-providers')
    items.value = Array.isArray(payload) ? payload : payload?.items || []
  } catch (error) {
    errorMessage.value = error?.message || '暂时无法读取系统 AI／API 配置。'
  } finally {
    loading.value = false
  }
}

async function submit() {
  saving.value = true
  errorMessage.value = ''
  successMessage.value = ''
  try {
    const payload = buildPayload()
    if (editingId.value) {
      await cloudApi.patch(`/api/admin/system-model-providers/${encodeURIComponent(editingId.value)}`, payload)
      successMessage.value = `已保存 ${form.provider} 的系统配置。`
    } else {
      await cloudApi.post('/api/admin/system-model-providers', { ...payload, api_key: form.api_key.trim() || null })
      successMessage.value = `已新增 ${form.provider}；API Key 只保存在云端加密仓库。`
    }
    resetForm()
    await load()
  } catch (error) {
    errorMessage.value = error?.message || '保存系统 AI／API 配置失败。'
  } finally {
    saving.value = false
  }
}

async function testCandidateKey() {
  if (!editingId.value || !form.api_key.trim()) return
  saving.value = true
  candidateTested.value = false
  candidateTestMessage.value = '正在测试候选 Key，尚未保存或启用…'
  try {
    const result = await cloudApi.post(`/api/admin/system-model-providers/${encodeURIComponent(editingId.value)}/test-candidate-key`, {
      api_key: form.api_key.trim(), operation: 'chat',
    })
    candidateTested.value = Boolean(result.ok)
    candidateTestMessage.value = result.ok
      ? `候选 Key 连通成功 · ${result.elapsed_ms || 0}ms；确认后才会替换现有 Key。`
      : `候选 Key 未通过：${result.error_summary || result.status_code || '未知错误'}`
  } catch (error) {
    candidateTestMessage.value = error?.message || '候选 Key 测试失败。'
  } finally {
    saving.value = false
  }
}

async function activateCandidateKey() {
  if (!editingId.value || !candidateTested.value || !form.api_key.trim()) return
  if (!window.confirm('确认启用已通过测试的新 API Key？现有 Key 将被替换，但不会显示或写入日志。')) return
  saving.value = true
  try {
    await cloudApi.post(`/api/admin/system-model-providers/${encodeURIComponent(editingId.value)}/rotate-key`, { api_key: form.api_key.trim() })
    successMessage.value = '新 API Key 已启用；候选明文已从表单清除。'
    form.api_key = ''
    candidateTested.value = false
    candidateTestMessage.value = ''
    await load()
  } catch (error) {
    errorMessage.value = error?.message || '启用新 API Key 失败。'
  } finally {
    saving.value = false
  }
}

async function updateStatus(item, status) {
  await runItemAction(item, async () => {
    await cloudApi.patch(`/api/admin/system-model-providers/${encodeURIComponent(item.id)}`, { status })
    successMessage.value = `${item.provider} 已${status === 'active' ? '启用' : '停用'}。`
  }, '更新供应商状态失败。')
}

async function setActive(item) {
  await runItemAction(item, async () => {
    await cloudApi.post(`/api/admin/system-model-providers/${encodeURIComponent(item.id)}/set-active`, {})
    successMessage.value = `${item.provider} 已设为当前系统主供应商。`
  }, '设为系统主供应商失败。')
}

async function testProvider(item, operation) {
  savingId.value = item.id
  errorMessage.value = ''
  testResults[item.id] = `正在测试${operationLabel(operation)}…`
  try {
    const result = await cloudApi.post(`/api/admin/system-model-providers/${encodeURIComponent(item.id)}/test`, { operation })
    testResults[item.id] = result.ok
      ? `${operationLabel(operation)}连接成功 · ${result.elapsed_ms || 0}ms`
      : `${operationLabel(operation)}测试失败 · ${result.error_summary || result.status_code || '未知错误'}`
    await load()
  } catch (error) {
    testResults[item.id] = error?.message || `${operationLabel(operation)}测试失败。`
  } finally {
    savingId.value = ''
  }
}

async function runItemAction(item, action, fallback) {
  savingId.value = item.id
  errorMessage.value = ''
  successMessage.value = ''
  try {
    await action()
    await load()
  } catch (error) {
    errorMessage.value = error?.message || fallback
  } finally {
    savingId.value = ''
  }
}

function editProvider(item) {
  const routes = item.config?.routes || {}
  editingId.value = item.id
  candidateTested.value = false
  candidateTestMessage.value = ''
  Object.assign(form, {
    provider: item.provider,
    label: item.config?.label || item.label || item.provider,
    base_url: item.config?.base_url || '',
    auth_type: item.config?.auth?.type || 'bearer',
    api_key: '',
    status: item.status || 'disabled',
  })
  assignRoute(form.chat, routes.chat, defaults.chat)
  assignRoute(form.image, routes.image, defaults.image)
  assignRoute(form.responses, routes.responses, defaults.responses)
  document.querySelector('#provider-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function assignRoute(target, source, fallback) {
  Object.assign(target, fallback, source || {})
}

function buildPayload() {
  return {
    provider: form.provider,
    label: form.label || null,
    base_url: form.base_url,
    auth_type: form.auth_type,
    status: form.status,
    chat: routePayload(form.chat),
    image: routePayload(form.image),
    responses: routePayload(form.responses),
  }
}

function routePayload(route) {
  return {
    enabled: Boolean(route.enabled),
    request_mode: route.request_mode,
    endpoint: route.endpoint,
    model: route.model,
    timeout_seconds: Number(route.timeout_seconds) || 180,
    default_parameters: {},
  }
}

function resetForm() {
  Object.assign(form, structuredClone(defaults))
  editingId.value = ''
  candidateTested.value = false
  candidateTestMessage.value = ''
}

function route(item, operation) { return item.config?.routes?.[operation] || {} }
function operationLabel(value) { return ({ chat: '文本', image: '图片', responses: 'Responses' })[value] || value }
function formatTime(value) {
  if (!value) return '尚未测试'
  return new Intl.DateTimeFormat('zh-CN', { dateStyle: 'short', timeStyle: 'short', hour12: false }).format(new Date(value))
}
</script>

<template>
  <div class="api-management">
    <section class="api-intro">
      <div>
        <p class="section-kicker">SYSTEM AI ROUTING</p>
        <h2>AI／API 管理</h2>
        <p>集中管理全平台的文本、图片与 Responses 路由。密钥只写入云端加密仓库，页面和接口永不返回明文。</p>
      </div>
      <button class="secondary-button" type="button" :disabled="loading" @click="load">{{ loading ? '读取中…' : '刷新配置' }}</button>
    </section>

    <p v-if="errorMessage" class="panel-error" role="alert">{{ errorMessage }}</p>
    <p v-if="successMessage" class="panel-success" role="status">{{ successMessage }}</p>

    <section class="provider-list" aria-labelledby="provider-list-title">
      <div class="section-heading"><div><span>01 / PROVIDERS</span><h2 id="provider-list-title">系统供应商</h2></div><strong>{{ items.length }} 个</strong></div>
      <div v-if="items.length" class="provider-grid">
        <article v-for="item in items" :key="item.id" class="provider-card" :class="item.status">
          <header>
            <div><span class="provider-code">{{ item.provider }}</span><h3>{{ item.config?.label || item.label || item.provider }}</h3></div>
            <span class="status-badge">{{ item.status === 'active' ? '系统启用中' : '已停用' }}</span>
          </header>
          <dl>
            <div><dt>Base URL</dt><dd>{{ item.config?.base_url || '未配置' }}</dd></div>
            <div><dt>API Key</dt><dd>{{ item.key_configured ? `已配置 ${item.key_hint || ''}` : '未配置' }}</dd></div>
            <div><dt>Key 更新时间</dt><dd>{{ formatTime(item.updated_at) }}（修改人见审计记录）</dd></div>
            <div><dt>文本模型</dt><dd>{{ route(item, 'chat').model || '未配置' }}</dd></div>
            <div><dt>图片模型</dt><dd>{{ route(item, 'image').enabled ? (route(item, 'image').model || '已启用') : '未启用' }}</dd></div>
            <div><dt>最近测试</dt><dd>{{ formatTime(item.last_test_at) }} · {{ item.last_test_elapsed_ms ?? '—' }}ms</dd></div>
          </dl>
          <p v-if="item.last_error_summary" class="provider-error">{{ item.last_error_summary }}</p>
          <p v-if="testResults[item.id]" class="test-result" role="status">{{ testResults[item.id] }}</p>
          <div class="provider-actions">
            <button type="button" :disabled="savingId === item.id" @click="editProvider(item)">编辑</button>
            <button v-if="item.status !== 'active'" type="button" :disabled="savingId === item.id" @click="setActive(item)">设为启用</button>
            <button v-else type="button" :disabled="savingId === item.id" @click="updateStatus(item, 'disabled')">停用</button>
            <button type="button" :disabled="savingId === item.id || !route(item, 'chat').enabled" @click="testProvider(item, 'chat')">测文本</button>
            <button type="button" :disabled="savingId === item.id || !route(item, 'image').enabled" @click="testProvider(item, 'image')">测图片</button>
            <button type="button" :disabled="savingId === item.id || !route(item, 'responses').enabled" @click="testProvider(item, 'responses')">测 Responses</button>
          </div>
        </article>
      </div>
      <div v-else class="empty-provider">{{ loading ? '正在读取系统供应商…' : '尚未配置系统供应商。请先新增并完成连通性测试。' }}</div>
    </section>

    <section id="provider-editor" class="provider-editor" aria-labelledby="provider-editor-title">
      <div class="section-heading"><div><span>02 / CONFIGURATION</span><h2 id="provider-editor-title">{{ formTitle }}</h2></div><button v-if="editingId" class="text-action" type="button" @click="resetForm">取消编辑</button></div>
      <form @submit.prevent="submit">
        <label>供应商代码<input v-model.trim="form.provider" required maxlength="80" :disabled="Boolean(editingId)" placeholder="deepseek"></label>
        <label>显示名称<input v-model.trim="form.label" maxlength="160" placeholder="企业可理解的名称"></label>
        <label class="wide">Base URL<input v-model.trim="form.base_url" required type="url" placeholder="https://api.example.com"></label>
        <label>认证方式<select v-model="form.auth_type"><option value="bearer">Bearer Token</option></select></label>
        <label>{{ editingId ? '新 API Key（留空不更换）' : 'API Key' }}<input v-model="form.api_key" :required="!editingId" type="password" autocomplete="new-password" :placeholder="editingId ? '输入候选 Key，先测试再确认启用' : '只写入云端加密仓库'" @input="candidateTested = false; candidateTestMessage = ''"></label>
        <div v-if="editingId" class="candidate-actions">
          <button type="button" :disabled="saving || !form.api_key.trim()" @click="testCandidateKey">1. 测试候选 Key</button>
          <button type="button" :disabled="saving || !candidateTested" @click="activateCandidateKey">2. 确认启用新 Key</button>
          <p v-if="candidateTestMessage" role="status">{{ candidateTestMessage }}</p>
        </div>

        <fieldset v-for="operation in ['chat', 'image', 'responses']" :key="operation">
          <legend>{{ operationLabel(operation) }}</legend>
          <label class="toggle"><input v-model="form[operation].enabled" type="checkbox">启用此路由</label>
          <label>请求协议<select v-model="form[operation].request_mode"><option v-if="operation === 'chat'" value="openai_chat_completions">OpenAI Chat Completions</option><option v-if="operation !== 'image'" value="openai_responses">OpenAI Responses</option><option v-if="operation === 'image'" value="openai_images_generations">OpenAI Images Generations</option></select></label>
          <label>Endpoint<input v-model.trim="form[operation].endpoint" :required="form[operation].enabled" placeholder="/chat/completions"></label>
          <label>模型<input v-model.trim="form[operation].model" :required="form[operation].enabled" placeholder="模型名称"></label>
          <label>超时（秒）<input v-model.number="form[operation].timeout_seconds" type="number" min="1" max="900"></label>
        </fieldset>

        <label>保存状态<select v-model="form.status"><option value="disabled">停用待测（建议）</option><option value="active">保存后启用</option></select></label>
        <button class="primary-button" type="submit" :disabled="saving">{{ saving ? '安全保存中…' : (editingId ? '保存修改' : '新增系统供应商') }}</button>
      </form>
      <p class="security-note">安全规则：API Key 不可查看、不可复制；更换 Key 会保留审计记录，但不会把旧 Key 写入日志。</p>
    </section>
  </div>
</template>

<style scoped>
.api-management { display: grid; gap: 18px; }
.api-intro, .provider-list, .provider-editor { padding: 24px; border: 1px solid #ddd7ef; border-radius: 22px; background: #fff; box-shadow: 0 18px 50px rgba(63, 47, 115, .08); }
.api-intro, .section-heading { display: flex; align-items: start; justify-content: space-between; gap: 20px; }
.api-intro h2, .section-heading h2 { margin: 4px 0 8px; }.api-intro p { max-width: 760px; margin-bottom: 0; color: #657078; line-height: 1.7; }
.section-kicker, .section-heading span { color: #6750a4; font-size: 11px; font-weight: 900; letter-spacing: .15em; }.section-heading strong { color: #3f2f73; }
.provider-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(330px, 1fr)); gap: 14px; margin-top: 18px; }
.provider-card { padding: 18px; border: 1px solid #e5e0f2; border-radius: 17px; background: #fbfaff; }.provider-card.active { border-color: #9c8bc9; box-shadow: inset 4px 0 #6750a4; }.provider-card header { display: flex; justify-content: space-between; gap: 12px; }.provider-card h3 { margin: 6px 0 0; }.provider-code, .status-badge { color: #6750a4; font-size: 11px; font-weight: 900; }.status-badge { height: fit-content; padding: 5px 8px; border-radius: 99px; background: #eee9fb; }
.provider-card dl { display: grid; gap: 8px; margin: 18px 0; }.provider-card dl div { display: grid; grid-template-columns: 92px minmax(0, 1fr); gap: 10px; }.provider-card dt { color: #7b8490; font-size: 12px; }.provider-card dd { margin: 0; overflow-wrap: anywhere; font-size: 13px; font-weight: 700; }
.provider-actions { display: flex; flex-wrap: wrap; gap: 8px; }.provider-actions button, .secondary-button, .text-action { padding: 8px 10px; border: 1px solid #cfc5e6; border-radius: 9px; color: #3f2f73; background: #fff; font-weight: 800; cursor: pointer; }.provider-actions button:disabled { opacity: .45; cursor: not-allowed; }
.provider-error, .panel-error { color: #8b3732; background: #fff1f0; }.provider-error, .test-result, .panel-error, .panel-success { padding: 10px 12px; border-radius: 10px; font-size: 13px; }.test-result, .panel-success { color: #176456; background: #effaf6; }.empty-provider { margin-top: 18px; padding: 30px; border: 1px dashed #cfc5e6; border-radius: 14px; color: #6f7480; text-align: center; }
.provider-editor form { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; margin-top: 18px; }.provider-editor label { display: grid; gap: 7px; color: #5f6670; font-size: 13px; font-weight: 800; }.provider-editor input, .provider-editor select { min-width: 0; min-height: 43px; padding: 9px 11px; border: 1px solid #d8d1e8; border-radius: 10px; background: #fff; font: inherit; }.provider-editor .wide { grid-column: 1 / -1; }.provider-editor fieldset { display: grid; grid-column: 1 / -1; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; padding: 16px; border: 1px solid #e5e0f2; border-radius: 14px; }.provider-editor legend { padding: 0 8px; color: #3f2f73; font-weight: 900; }.provider-editor .toggle { display: flex; align-items: center; gap: 8px; }.provider-editor .toggle input { min-height: auto; }.primary-button { min-height: 45px; padding: 10px 16px; border: 0; border-radius: 10px; color: #fff; background: #4b3482; font-weight: 900; cursor: pointer; }.primary-button:disabled { opacity: .55; }.security-note { margin: 16px 0 0; color: #6f7480; font-size: 12px; }
.candidate-actions { display: grid; align-content: end; gap: 8px; }.candidate-actions button { min-height: 40px; border: 1px solid #cfc5e6; border-radius: 9px; color: #3f2f73; background: #fff; font-weight: 800; }.candidate-actions p { margin: 0; color: #176456; font-size: 12px; }
@media (max-width: 760px) { .api-intro, .section-heading { display: grid; }.provider-grid, .provider-editor form { grid-template-columns: 1fr; }.provider-editor .wide { grid-column: auto; }.provider-editor fieldset { grid-template-columns: 1fr; }.provider-card dl div { grid-template-columns: 80px minmax(0, 1fr); } }
</style>
